import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { errText } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ShieldCheck, Buildings, Lock, ArrowUpRight } from "@phosphor-icons/react";
import { toast } from "sonner";

// Dedicated firm-staff sign-in page.
// - Auth backend expects `context: "firm"` on POST /auth/login.
// - If a client account tries to sign in here, the backend returns
//   { wrong_context: "client" } (no MFA token issued) and we silently
//   redirect to /login carrying the email in the query string.
// - Distinct dark visual identity from the client portal (which uses the
//   warm sand/serif Artha branding).
const serif = { fontFamily: "'Playfair Display', Georgia, serif" };

export default function FirmLogin() {
  const { login, completeAuth } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState("creds"); // creds | setup | verify | backup | reset
  const [email, setEmail] = useState(() => new URLSearchParams(window.location.search).get("email") || "");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [mfa, setMfa] = useState(null);
  const [backupCodes, setBackupCodes] = useState([]);
  const [pendingUser, setPendingUser] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  // Firm users always land on /dashboard after login. This page never sees a
  // successful "client" auth because the backend rejects them at step 1.
  const goHome = () => navigate("/dashboard");

  useEffect(() => {
    // Nice touch: focus the password field if the email came in via URL param
    // (happens when a firm user typed at /login and got auto-redirected here).
    if (email) {
      const el = document.querySelector('[data-testid="firm-login-password-input"]');
      if (el) el.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitCreds = async (em, pw) => {
    setBusy(true); setError("");
    try {
      const data = await login(em, pw, "firm");
      if (data.wrong_context === "client") {
        // Silently bounce a client account back to the client portal login.
        navigate(`/login?email=${encodeURIComponent(em)}`, { replace: true });
        return;
      }
      setMfa(data);
      setCode("");
      setStep(data.mfa_setup_required ? "setup" : "verify");
    } catch (e) { setError(errText(e)); } finally { setBusy(false); }
  };

  const submitCode = async () => {
    setBusy(true); setError("");
    try {
      const endpoint = step === "setup" ? "/auth/mfa/enable" : "/auth/mfa/verify";
      const { data } = await api.post(endpoint, { mfa_token: mfa.mfa_token, code });
      if (data.backup_codes) {
        setBackupCodes(data.backup_codes);
        setPendingUser(data);
        setStep("backup");
      } else {
        completeAuth(data);
        goHome();
      }
    } catch (e) { setError(errText(e)); } finally { setBusy(false); }
  };

  const submitReset = async () => {
    setBusy(true); setError("");
    try {
      await api.post("/auth/password-reset", { email, code, new_password: newPassword });
      toast.success("Password reset — sign in with your new password");
      setPassword(""); setCode(""); setNewPassword("");
      setStep("creds");
    } catch (e) { setError(errText(e)); } finally { setBusy(false); }
  };

  return (
    <div
      data-testid="firm-login-page"
      className="min-h-screen flex text-slate-100"
      style={{ background: "radial-gradient(circle at 20% 0%, #1a2540 0%, #0b1226 55%, #05070f 100%)" }}>
      {/* Left panel — brand & context */}
      <div className="hidden lg:flex flex-1 items-center justify-center p-12 border-r border-white/5">
        <div className="max-w-md space-y-6">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-lg flex items-center justify-center bg-white/95 p-1">
              <img src="/artha-logo.png" alt="Artha" className="w-full h-full object-contain" />
            </div>
            <div>
              <p className="text-sm font-medium tracking-wide">Artha Business Solutions LLC</p>
              <p className="text-[11px] text-slate-400">Tax Advisory &amp; Preparation</p>
            </div>
          </div>
          <div>
            <h2 className="text-3xl leading-tight tracking-tight" style={serif}>Firm access to your workbench, dashboards, and every client file.</h2>
            <p className="mt-4 text-sm text-slate-300/80 leading-relaxed">
              This portal is for firm staff, partners, managers, CPAs, and admins. If you're a client, we'll route you to the correct sign-in automatically.
            </p>
          </div>
          <ul className="space-y-2 text-xs text-slate-300/80">
            <li className="flex items-center gap-2"><Lock size={13} className="text-emerald-300" /> MFA enforced for every staff role</li>
            <li className="flex items-center gap-2"><ShieldCheck size={13} className="text-emerald-300" /> Every request audit-logged &amp; IP-fingerprinted</li>
          </ul>
        </div>
      </div>

      {/* Right panel — form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-sm fade-up">
          <div className="lg:hidden flex items-center gap-2.5 mb-9">
            <div className="w-10 h-10 rounded-lg bg-white/95 flex items-center justify-center p-1">
              <img src="/artha-logo.png" alt="Artha" className="w-full h-full object-contain" />
            </div>
            <div>
              <p className="font-medium text-sm">Artha Business Solutions LLC</p>
              <p className="text-[11px] text-slate-400">Firm portal · MFA enforced</p>
            </div>
          </div>

          {step === "creds" && (
            <>
              <h1 className="text-2xl mb-1" style={{ ...serif, color: "#F8FAFC" }}>Staff Sign-In</h1>
              <p className="text-sm mb-6 text-slate-300">Admin / CPA / Staff / Partner / Manager · one shared entry point.</p>
              <form onSubmit={(e) => { e.preventDefault(); submitCreds(email, password); }} className="space-y-3">
                <Input
                  data-testid="firm-login-email-input"
                  type="email" placeholder="you@arthataxes.com"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  className="bg-white/5 border-white/10 text-slate-100 placeholder:text-slate-500 focus-visible:ring-emerald-400"
                  required />
                <Input
                  data-testid="firm-login-password-input"
                  type="password" placeholder="Password"
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  className="bg-white/5 border-white/10 text-slate-100 placeholder:text-slate-500 focus-visible:ring-emerald-400"
                  required />
                {error && <p data-testid="firm-login-error" className="text-xs text-rose-400">{error}</p>}
                <Button
                  data-testid="firm-login-submit-btn"
                  type="submit"
                  className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold"
                  disabled={busy}>
                  {busy ? "Signing in…" : "Continue"}
                </Button>
                <button
                  type="button" data-testid="firm-login-forgot-link"
                  onClick={() => { setError(""); setStep("reset"); }}
                  className="w-full text-xs text-slate-400 hover:text-slate-200 transition-colors duration-150">
                  Forgot password? Reset with your authenticator →
                </button>
                <button
                  type="button" data-testid="firm-login-client-link"
                  onClick={() => navigate("/login")}
                  className="w-full text-xs text-slate-500 hover:text-slate-300 flex items-center justify-center gap-1 transition-colors duration-150">
                  Are you a client? <span className="text-emerald-300">Client portal <ArrowUpRight size={11} weight="bold" /></span>
                </button>
              </form>
            </>
          )}

          {(step === "setup" || step === "verify") && (
            <>
              <h1 className="text-2xl mb-1 flex items-center gap-2" style={{ ...serif, color: "#F8FAFC" }}>
                <ShieldCheck size={22} className="text-emerald-300" />
                {step === "setup" ? "Set up MFA" : "Enter your code"}
              </h1>
              <p className="text-sm mb-4 text-slate-400">
                {step === "setup"
                  ? "Scan the QR with Google Authenticator / 1Password / Authy, then enter the 6-digit code."
                  : "Enter the 6-digit code from your authenticator app, or a one-time backup code."}
              </p>
              {step === "setup" && mfa?.qr_data_uri && (
                <div className="rounded-lg border border-white/10 bg-white/5 p-4 flex flex-col items-center mb-4">
                  <img data-testid="firm-login-mfa-qr" src={mfa.qr_data_uri} alt="MFA QR code" className="w-44 h-44 rounded-md" />
                  <button
                    data-testid="firm-login-mfa-copy-btn"
                    onClick={() => { navigator.clipboard?.writeText(mfa?.secret).catch(() => {}); toast.success("Secret copied"); }}
                    className="mt-2 flex items-center gap-1 font-mono text-[11px] text-slate-400 hover:text-slate-200 transition-colors duration-150">
                    {mfa.secret}
                  </button>
                </div>
              )}
              <form onSubmit={(e) => { e.preventDefault(); submitCode(); }} className="space-y-3">
                <Input
                  data-testid="firm-login-mfa-input"
                  placeholder="123456"
                  value={code} onChange={(e) => setCode(e.target.value)}
                  className="bg-white/5 border-white/10 text-slate-100 placeholder:text-slate-500 font-mono focus-visible:ring-emerald-400"
                  required autoFocus />
                {error && <p data-testid="firm-login-mfa-error" className="text-xs text-rose-400">{error}</p>}
                <Button
                  data-testid="firm-login-mfa-submit-btn"
                  type="submit"
                  className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold"
                  disabled={busy || code.length < 6}>
                  {busy ? "Verifying…" : "Verify & sign in"}
                </Button>
                <button
                  type="button"
                  onClick={() => { setStep("creds"); setCode(""); setError(""); }}
                  className="w-full text-xs text-slate-400 hover:text-slate-200">
                  Back
                </button>
              </form>
            </>
          )}

          {step === "backup" && (
            <>
              <h1 className="text-2xl mb-1 flex items-center gap-2" style={{ ...serif, color: "#F8FAFC" }}>
                <ShieldCheck size={22} className="text-emerald-300" /> Save your backup codes
              </h1>
              <p className="text-sm mb-4 text-slate-400">Store these somewhere safe. Each code works once and lets you sign in if you lose your authenticator device.</p>
              <div className="rounded-lg border border-white/10 bg-white/5 p-4 mb-4 grid grid-cols-2 gap-1.5 font-mono text-xs text-slate-200" data-testid="firm-login-backup-codes">
                {backupCodes.map((c) => <div key={c}>{c}</div>)}
              </div>
              <Button
                data-testid="firm-login-backup-done-btn"
                className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold"
                onClick={() => { completeAuth(pendingUser); goHome(); }}>
                I've saved them — continue
              </Button>
            </>
          )}

          {step === "reset" && (
            <>
              <h1 className="text-2xl mb-1 flex items-center gap-2" style={{ ...serif, color: "#F8FAFC" }}>
                <ShieldCheck size={22} className="text-emerald-300" /> Reset password
              </h1>
              <p className="text-sm mb-6 text-slate-400">Verify with your authenticator, then choose a new password.</p>
              <form onSubmit={(e) => { e.preventDefault(); submitReset(); }} className="space-y-3">
                <Input
                  data-testid="firm-reset-email-input"
                  type="email" placeholder="you@arthataxes.com"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  className="bg-white/5 border-white/10 text-slate-100 placeholder:text-slate-500 focus-visible:ring-emerald-400"
                  required />
                <Input
                  data-testid="firm-reset-code-input"
                  placeholder="6-digit authenticator or backup code"
                  value={code} onChange={(e) => setCode(e.target.value)}
                  className="bg-white/5 border-white/10 text-slate-100 placeholder:text-slate-500 font-mono focus-visible:ring-emerald-400"
                  required />
                <Input
                  data-testid="firm-reset-password-input"
                  type="password" placeholder="New password (min 8 characters)"
                  value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                  className="bg-white/5 border-white/10 text-slate-100 placeholder:text-slate-500 focus-visible:ring-emerald-400"
                  required />
                {error && <p data-testid="firm-reset-error" className="text-xs text-rose-400">{error}</p>}
                <Button
                  data-testid="firm-reset-submit-btn"
                  type="submit"
                  className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold"
                  disabled={busy || !email || code.length < 6 || newPassword.length < 8}>
                  {busy ? "Resetting…" : "Reset password"}
                </Button>
                <button
                  type="button" data-testid="firm-reset-back-btn"
                  onClick={() => { setError(""); setStep("creds"); }}
                  className="w-full text-xs text-slate-400 hover:text-slate-200">Back to sign in</button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
